/**
 * ProductMultiOption – Custom Element
 *
 * Shopify supports up to 3 product options. To exceed this limit, multiple
 * logical options (e.g. Quality, Weight, Metal) are packed into one Shopify
 * option using "/" as a delimiter:
 *   option name  → "Quality/Weight/Metal"
 *   option value → "A/1.85/Rose Gold Over Sterling Silver"
 *
 * This component unpacks those combined options, renders one fieldset per
 * logical option, and cascades availability based on upstream selections.
 */
class ProductMultiOption extends HTMLElement {

    /** @type {HTMLTemplateElement | null} */
    #optionTemplate = null;

    /** @type {HTMLTemplateElement | null} */
    #valueTemplate = null;

    constructor() {
        super();

        /** @type {any} */
        this.data = null;
        /** @type {string[]} Logical option names after splitting "/" packed names */
        this.optionNames = [];
        /** @type {string[]} Selected value per logical option (parallel to optionNames) */
        this.selectedValues = [];

        this.shopDomain = this.getAttribute('data-shop-domain') || window.Shopify?.shop || location.hostname;
        this.productId = this.getAttribute('data-product-id');
        this.blockId = this.getAttribute('data-block-id');
        /** @type {string} Comma-separated initial selected values injected by Liquid */
        this.initialSelectedValues = this.getAttribute('data-selected-value') || '';

        /** @type {HTMLElement | null} */
        this._currentQualityTooltip = null;
        /** @type {HTMLButtonElement | null} */
        this._currentQualityTrigger = null;
        /** @type {((e: Event) => void) | null} */
        this._qualityTooltipDocClick = null;
        /** @type {(() => void) | null} */
        this._qualityTooltipScroll = null;
    }

    /**
     * Build the CDN image URL for a metal option value.
     * Rule: trim whitespace, replace "/" and spaces with "_", preserve case and other chars.
     * @param {string} value
     * @returns {string}
     */
    #buildMetalImageUrl(value) {
        const filename = String(value).trim().replace(/\//g, '_').replace(/ /g, '_');
        return `https://image.shoplc.com/groupingproductimages/${filename}.png`;
    }

    // ── Lifecycle ────────────────────────────────────────────────────────────────

    async connectedCallback() {
        await this.#init();
    }

    disconnectedCallback() {
        this.#teardownQualityTooltips();
    }

    async #init() {
        if (!this.productId) return;

        // Remove <template> elements from the live DOM; store refs for cloning
        this.#optionTemplate = this.querySelector('#product-option-template');
        this.#valueTemplate = this.querySelector('#product-option-value-template');
        if (this.#optionTemplate) this.#optionTemplate.remove();
        if (this.#valueTemplate) this.#valueTemplate.remove();

        // Window-level cache prevents re-fetching on re-mount (e.g. quick-view)
        const cacheKey = `pmo_${this.productId}`;
        /** @type {any} */
        const win = window;
        this.data = win[cacheKey] || (win[cacheKey] = await this.fetchAllProductData());

        if (this.data) {
            this.optionNames = this.data.optionNames;
            this.#parseInitialSelection();
            this.render();
        }
    }

    // ── Step 1: Fetch product data ───────────────────────────────────────────────

    /**
     * Fetches all options and variants from the app proxy (/apps/common/v1/multi-options).
     * @returns {Promise<any>}
     */
    async fetchAllProductData() {
        /** @type {any[]} */
        let allVariants = [];

        /** @type {string[]} */         let optionNames = [];
        /** @type {Record<string,string>} */ let optionsMap = {};
        /** @type {Record<string,any>} */   let swatchMap = {};
        /** @type {Record<string,any>} */   let valueMetadata = {};
        /** @type {number[]} */             let multiOptionIndices = [];

        try {
            const response = await fetch(`/apps/common/v1/multi-options?product=${this.productId}`);
            const result = await response.json();

            if (!result.success) throw new Error(result.message || 'API request failed');

            const product = result.data;
            if (!product) return null;

            // Parse option metadata
            if (product.options) {
                this.#parseOptionLabels(product.options, optionNames, optionsMap, swatchMap, valueMetadata, multiOptionIndices);
            }

            if (product.variants?.nodes) {
                this.#accumulateVariants(product.variants.nodes, allVariants, swatchMap);
            }

            return { optionNames, variants: allVariants, optionsMap, swatchMap, valueMetadata, multiOptionIndices };

        } catch (e) {
            console.error('ProductMultiOption – fetch error:', e);
            return null;
        }
    }

    // ── Step 2: Parse multi-option labels ───────────────────────────────────────

    /**
     * Expands "/" packed Shopify options into individual logical option names/values.
     *   "Quality/Weight/Metal"          → ["Quality", "Weight", "Metal"]
     *   "A/1.85/Rose Gold Over Sterling Silver" → ["A", "1.85", "Rose Gold Over Sterling Silver"]
     * Non-packed options (e.g. "Size") pass through unchanged.
     *
     * @param {any[]} shopifyOptions
     * @param {string[]} optionNames
     * @param {Record<string,string>} optionsMap
     * @param {Record<string,any>} swatchMap
     * @param {Record<string,any>} valueMetadata
     * @param {number[]} multiOptionIndices
     */
    #parseOptionLabels(shopifyOptions, optionNames, optionsMap, swatchMap, valueMetadata, multiOptionIndices) {
        shopifyOptions.forEach((/** @type {any} */ opt, /** @type {number} */ optIndex) => {
            const isMulti = opt.name.includes('/');
            const logicalNames = isMulti ? opt.name.split('/') : [opt.name];

            // Track indices originating from a packed option; consumed by variant-picker.js
            if (isMulti) {
                logicalNames.forEach((/** @type {string} */ _, /** @type {number} */ i) =>
                    multiOptionIndices.push(optionNames.length + i)
                );
            }

            optionNames.push(...logicalNames);
            logicalNames.forEach((/** @type {string} */ name) => { if (!valueMetadata[name]) valueMetadata[name] = {}; });

            opt.optionValues.forEach((/** @type {any} */ ov) => {
                const numericId = ov.id.split('/').pop();
                const variantId = ov.firstSelectableVariant?.id.split('/').pop();

                optionsMap[ov.name] = numericId || '';

                if (isMulti) {
                    const valueParts = ov.name.split('/');

                    valueParts.forEach((/** @type {string} */ part, /** @type {number} */ i) => {
                        // Keep first occurrence so the earliest selectable variant ID is used
                        if (!valueMetadata[logicalNames[i]][part]) {
                            valueMetadata[logicalNames[i]][part] = { id: numericId, variantId, position: optIndex + 1 };
                        }
                    });

                    // Swatch is keyed to the first sub-value (e.g. Quality grade)
                    const firstPart = valueParts[0];
                    if (ov.swatch && !swatchMap[firstPart]) {
                        swatchMap[firstPart] = { image: ov.swatch.image?.url, color: ov.swatch.color };
                    }
                } else {
                    valueMetadata[opt.name][ov.name] = { id: numericId, variantId, position: optIndex + 1 };
                }
            });
        });
    }

    /**
     * Normalises raw variant nodes into { id, available, splitValues, option }
     * and accumulates them. Packed option values are split on "/" so splitValues
     * aligns 1-to-1 with logicalOptionNames.
     *
     * @param {any[]} nodes
     * @param {any[]} allVariants
     * @param {Record<string,any>} swatchMap
     */
    #accumulateVariants(nodes, allVariants, swatchMap) {
        nodes.forEach((/** @type {any} */ node) => {
            const variantId = node.id.split('/').pop();

            /** @type {string[]} */
            const splitValues = [];
            node.selectedOptions.forEach((/** @type {any} */ so) => {
                so.name.includes('/') ? splitValues.push(...so.value.split('/')) : splitValues.push(so.value);
            });

            // Fallback swatch from variant image when no API swatch is available
            if (splitValues[0] && !swatchMap[splitValues[0]] && node.image) {
                swatchMap[splitValues[0]] = { image: node.image.url };
            }

            allVariants.push({
                id: variantId,
                available: node.availableForSale,
                splitValues,
                option: node.selectedOptions.map((/** @type {any} */ so) => so.value).join(' / ')
            });
        });
    }

    // ── Initial selection ────────────────────────────────────────────────────────

    /**
     * Parses the Liquid-injected initial selection string into selectedValues[].
     * Input format: "A/1.85/Rose Gold Over Sterling Silver,10"
     * Output: ["A", "1.85", "Rose Gold Over Sterling Silver", "10"]
     */
    #parseInitialSelection() {
        if (this.initialSelectedValues && this.optionNames.length > 0) {
            /** @type {string[]} */
            const flatValues = [];
            this.initialSelectedValues.split(',').forEach(part => {
                part.includes('/') ? flatValues.push(...part.split('/')) : flatValues.push(part);
            });
            this.selectedValues = new Array(this.optionNames.length).fill('');
            flatValues.forEach((val, i) => { if (i < this.selectedValues.length) this.selectedValues[i] = val; });
        } else {
            this.selectedValues = new Array(this.optionNames.length).fill('');
        }
    }

    // ── Rendering ────────────────────────────────────────────────────────────────

    render() {
        const mount = this.querySelector('.product-multi-option__ui-mount');
        if (!mount || !this.data) return;

        mount.innerHTML = '';
        const container = document.createElement('div');
        container.className = 'product-multi-option__container';

        this.optionNames.forEach((name, index) => {
            const fieldset = this.#createFieldset(name, index);
            if (fieldset) container.appendChild(fieldset);
        });

        mount.appendChild(container);
        this.#initQualityTooltips();
    }

    /**
     * @param {string} name
     * @param {number} index
     * @returns {HTMLFieldSetElement | null}
     */
    #createFieldset(name, index) {
        if (!this.#optionTemplate) return null;

        const fragment = /** @type {DocumentFragment} */ (this.#optionTemplate.content.cloneNode(true));
        const fieldset = /** @type {HTMLFieldSetElement | null} */ (fragment.querySelector('fieldset'));
        const nameSpan = fragment.querySelector('.variant-option__name');
        const valSpan = fragment.querySelector('.variant-option__swatch-value');

        if (!fieldset || !nameSpan || !valSpan) return null;

        fieldset.dataset.fieldsetIndex = index.toString();
        fieldset.setAttribute('ref', 'fieldsets[]');
        nameSpan.textContent = name;
        valSpan.textContent = this.selectedValues[index] || '';

        const nameSlug = name.toLowerCase().replace(/\s+/g, '-');
        const isMetalOption = nameSlug === 'metal';

        if (index === 0 || isMetalOption) {
            fieldset.classList.add('variant-option--swatches');
            fieldset.classList.add(`${nameSlug}-swatches`);
        }

        if (fieldset.classList.contains('quality-swatches')) {
            this.#attachQualityTooltip(fieldset);
        }

        this.#getUniqueValuesAtIndex(index).forEach((val, valIndex) => {
            const valEl = this.#createValue(name, val, index, valIndex);
            if (valEl) fieldset.appendChild(valEl);
        });

        return fieldset;
    }

    /**
     * Returns unique values for the option at `index`, filtered to only those
     * reachable given the currently selected upstream values (indices 0…index-1).
     *
     * @param {number} index
     * @returns {string[]}
     */
    #getUniqueValuesAtIndex(index) {
        if (!this.data) return [];

        const eligible = this.data.variants.filter((/** @type {any} */ v) => {
            for (let i = 0; i < index; i++) {
                const sel = this.selectedValues[i];
                if (sel !== '' && sel !== v.splitValues[i]) return false;
            }
            return true;
        });

        return [...new Set(eligible.map((/** @type {any} */ v) => v.splitValues[index]))].filter(Boolean);
    }

    /**
     * @param {string} optionName
     * @param {string} value
     * @param {number} index
     * @param {number} valIndex
     * @returns {HTMLElement | null}
     */
    #createValue(optionName, value, index, valIndex) {
        if (!this.#valueTemplate || !this.data) return null;

        const fragment = /** @type {DocumentFragment} */ (this.#valueTemplate.content.cloneNode(true));
        const wrapper = /** @type {HTMLElement | null} */ (fragment.querySelector('.variant-option__button-label__wrapper'));
        const input = /** @type {HTMLInputElement | null} */ (fragment.querySelector('input'));
        const label = fragment.querySelector('label');
        const text = fragment.querySelector('.variant-option__button-label__text');

        if (!wrapper || !input || !label || !text) return null;

        const metadata = this.data.valueMetadata?.[optionName]?.[value] || {};
        const isChecked = this.selectedValues[index] === value;

        // Input name mirrors Liquid pattern: {option}-{block.id}-{product.id}
        input.name = `${optionName}-${this.blockId || 'unknown'}-${this.productId || 'unknown'}`;
        input.value = value;
        input.checked = isChecked;
        input.setAttribute('aria-label', value);

        // Data attributes consumed by variant-picker.js
        input.dataset.previousChecked = 'false';
        input.dataset.fieldsetIndex = index.toString();
        input.dataset.inputIndex = valIndex.toString();
        input.dataset.inputId = `${metadata.position || (index + 1)}-${valIndex}`;
        input.dataset.optionValueId = metadata.id || '';
        input.dataset.variantId = metadata.variantId || '';
        input.dataset.currentChecked = isChecked ? 'true' : 'false';

        // Marks inputs from a packed option so variant-picker.js joins their values
        if (this.data.multiOptionIndices?.includes(index)) {
            input.dataset.multiOption = '';
        }

        text.textContent = value;

        const isAvailable = this.#checkAvailability(value, index);
        input.dataset.optionAvailable = isAvailable.toString();

        const icon = wrapper.querySelector('.variant-option__button-label__icon--ring');
        if (!isAvailable) {
            input.setAttribute('aria-disabled', 'true');
            if (icon) icon.classList.remove('hidden');
        } else {
            if (icon) icon.classList.add('hidden');
        }

        // Swatch rendering: Metal (any index) uses CDN URL with onerror fallback;
        // first option (non-Metal) uses swatchMap from API / variant image.
        const isMetalOption = optionName.toLowerCase() === 'metal';
        const swatchData = this.data.swatchMap[value];

        if (isMetalOption) {
            label.classList.add('variant-option__button-label--has-swatch');
            text.classList.add('hidden');

            const swatch = document.createElement('span');
            swatch.className = 'swatch swatch--unscaled swatch--metal-url';

            const imageUrl = this.#buildMetalImageUrl(value);
            const fallbackInitials = value.slice(0, 2).toUpperCase();
            swatch.innerHTML = `
                <img src="${imageUrl}" alt="${value.replace(/"/g, '&quot;')}" class="swatch__image" loading="lazy" width="80" height="80">
                <span class="swatch-variant__name swatch__fallback-initials" style="display:none;">${fallbackInitials}</span>
            `;
            const img = swatch.querySelector('img');
            const fallback = swatch.querySelector('.swatch__fallback-initials');
            if (img && fallback) {
                img.addEventListener('error', () => {
                    img.style.display = 'none';
                    /** @type {HTMLElement} */ (fallback).style.display = 'flex';
                }, { once: true });
            }

            label.appendChild(swatch);
        } else if (index === 0 && swatchData) {
            label.classList.add('variant-option__button-label--has-swatch');
            text.classList.add('hidden');

            const swatch = document.createElement('span');
            swatch.className = 'swatch swatch--unscaled';

            if (swatchData.image) {
                swatch.style.setProperty('--swatch-background', `url(${swatchData.image})`);
            } else if (swatchData.color) {
                swatch.style.setProperty('--swatch-background', swatchData.color);
            } else {
                const initials = document.createElement('span');
                initials.className = 'swatch-variant__name';
                initials.textContent = value.slice(0, 2).toUpperCase();
                swatch.appendChild(initials);
            }

            label.appendChild(swatch);
        }

        input.addEventListener('change', () => {
            this.selectedValues[index] = value;
            this.#handleSelectionChange();
        });

        return wrapper;
    }

    // ── Availability ─────────────────────────────────────────────────────────────

    /**
     * Returns true if at least one available variant matches the current
     * selection through `index` when `val` is placed at position `index`.
     *
     * @param {string} val
     * @param {number} index
     * @returns {boolean}
     */
    #checkAvailability(val, index) {
        if (!this.data) return false;
        const temp = [...this.selectedValues];
        temp[index] = val;
        return this.data.variants.some((/** @type {any} */ v) =>
            temp.slice(0, index + 1).every((s, i) => s === '' || s === v.splitValues[i]) && v.available
        );
    }

    // ── Selection change ─────────────────────────────────────────────────────────

    #handleSelectionChange() {
        let needsRerender = false;

        this.optionNames.forEach((name, index) => {
            const fieldset = this.querySelector(`fieldset[data-fieldset-index="${index}"]`);

            // Update the displayed selected-value label
            const valSpan = fieldset?.querySelector('.variant-option__swatch-value');
            if (valSpan) valSpan.textContent = this.selectedValues[index] || '';

            // Sync data-current-checked on all inputs in this fieldset
            fieldset?.querySelectorAll('input').forEach(input => {
                input.dataset.currentChecked = (input.value === this.selectedValues[index]) ? 'true' : 'false';
            });

            // Cascade: reset downstream selections that no longer exist in the
            // eligible value list for the new upstream combination.
            if (index > 0) {
                const eligibleValues = this.#getUniqueValuesAtIndex(index);
                const currentVal = this.selectedValues[index] ?? '';

                // Preserve the current value if it still exists (even if out of stock);
                // only reset when the value is absent from the filtered eligible list.
                if (!eligibleValues.includes(currentVal)) {
                    const firstAvailable = eligibleValues.find(val => {
                        const temp = [...this.selectedValues.slice(0, index), val];
                        return this.data.variants.some((/** @type {any} */ v) =>
                            temp.every((s, i) => s === v.splitValues[i]) && v.available
                        );
                    });
                    this.selectedValues[index] = firstAvailable ?? eligibleValues[0] ?? '';
                    needsRerender = true;
                }
            }
        });

        if (needsRerender) {
            this.render();
            return;
        }

        this.updateAvailability();

        // Trigger the parent variant-picker once all options have a value
        if (this.selectedValues.every(v => v !== '')) {
            const combined = this.selectedValues.join('/');
            const parent = this.closest('variant-picker');
            const hiddenInput = /** @type {HTMLInputElement | null | undefined} */ (
                parent?.querySelector(`input[data-multi-option][value="${combined}"]`)
            );
            if (hiddenInput) {
                hiddenInput.checked = true;
                hiddenInput.click();
            }
        }
    }

    /**
     * Updates data-option-available, aria-disabled, and the sold-out icon for
     * every input without a full re-render.
     */
    updateAvailability() {
        this.querySelectorAll('fieldset').forEach(fieldset => {
            const fs = /** @type {HTMLElement} */ (fieldset);
            const index = parseInt(fs.dataset.fieldsetIndex || '0');

            fs.querySelectorAll('input').forEach(input => {
                const isAvailable = this.#checkAvailability(input.value, index);
                const icon = input
                    .closest('.variant-option__button-label__wrapper')
                    ?.querySelector('.variant-option__button-label__icon--ring');

                input.dataset.optionAvailable = isAvailable.toString();

                if (!isAvailable) {
                    input.setAttribute('aria-disabled', 'true');
                    if (icon) icon.classList.remove('hidden');
                } else {
                    input.removeAttribute('aria-disabled');
                    if (icon) icon.classList.add('hidden');
                }
            });
        });
    }

    // ── Quality row tooltip (same behavior as product-specification tooltip in product-accordion) ──

    /**
     * @param {HTMLFieldSetElement} fieldset
     */
    #attachQualityTooltip(fieldset) {
        const tpl = this.querySelector('#product-option-quality-tooltip-template');
        const legend = fieldset.querySelector('legend.variant-option__legend');
        if (!tpl || !legend) return;

        const tooltipId = `quality-tooltip-${this.blockId || 'b'}-${this.productId || 'p'}`;
        const fragment = /** @type {DocumentFragment} */ (tpl.content.cloneNode(true));
        const wrapper = fragment.querySelector('.variant-option__legend-tooltip');
        const trigger = fragment.querySelector('[data-quality-tooltip-trigger]');
        const panel = fragment.querySelector('[data-quality-tooltip-panel]');
        if (!wrapper || !trigger || !(panel instanceof HTMLElement)) return;

        trigger.setAttribute('aria-controls', tooltipId);
        panel.id = tooltipId;

        legend.appendChild(wrapper);
    }

    #teardownQualityTooltips() {
        if (this._qualityTooltipDocClick) {
            document.removeEventListener('click', this._qualityTooltipDocClick);
            this._qualityTooltipDocClick = null;
        }
        if (this._qualityTooltipScroll) {
            window.removeEventListener('scroll', this._qualityTooltipScroll);
            this._qualityTooltipScroll = null;
        }
        this._currentQualityTooltip = null;
        this._currentQualityTrigger = null;
    }

    #initQualityTooltips() {
        this.#teardownQualityTooltips();

        const triggers = this.querySelectorAll('[data-quality-tooltip-trigger]');
        if (triggers.length === 0) return;

        triggers.forEach((/** @type {Element} */ raw) => {
            const trigger = /** @type {HTMLButtonElement} */ (raw);
            const tooltip = document.getElementById(trigger.getAttribute('aria-controls') || '');
            if (!tooltip) return;

            const hoverRoot = trigger.closest('.variant-option__legend-tooltip');
            if (hoverRoot) {
                hoverRoot.addEventListener('mouseenter', () => {
                    if (!this.#isTouchDevice()) {
                        this.#showQualityTooltip(trigger, tooltip);
                    }
                });

                hoverRoot.addEventListener('mouseleave', (e) => {
                    if (this.#isTouchDevice()) return;
                    const rel = /** @type {MouseEvent} */ (e).relatedTarget;
                    if (rel instanceof Node && (tooltip === rel || tooltip.contains(rel))) {
                        return;
                    }
                    this.#hideQualityTooltip(trigger, tooltip);
                });
            }

            tooltip.addEventListener('mouseenter', () => {
                if (!this.#isTouchDevice()) {
                    this.#showQualityTooltip(trigger, tooltip);
                }
            });

            tooltip.addEventListener('mouseleave', (e) => {
                if (this.#isTouchDevice()) return;
                const rel = /** @type {MouseEvent} */ (e).relatedTarget;
                if (rel instanceof Node && hoverRoot && hoverRoot.contains(rel)) {
                    return;
                }
                this.#hideQualityTooltip(trigger, tooltip);
            });

            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
                if (isExpanded) {
                    this.#hideQualityTooltip(trigger, tooltip);
                } else {
                    this.#showQualityTooltip(trigger, tooltip);
                }
            });

            trigger.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
                    if (isExpanded) {
                        this.#hideQualityTooltip(trigger, tooltip);
                    } else {
                        this.#showQualityTooltip(trigger, tooltip);
                    }
                } else if (e.key === 'Escape') {
                    this.#hideQualityTooltip(trigger, tooltip);
                }
            });
        });

        this._qualityTooltipDocClick = (e) => {
            const t = /** @type {HTMLElement} */ (e.target);
            if (
                t.closest('.product-specification__tooltip-trigger') ||
                t.closest('.product-specification__tooltip')
            ) {
                return;
            }
            triggers.forEach((/** @type {Element} */ raw) => {
                const trigger = /** @type {HTMLButtonElement} */ (raw);
                const tooltip = document.getElementById(trigger.getAttribute('aria-controls') || '');
                if (tooltip && !tooltip.hidden) {
                    this.#hideQualityTooltip(trigger, tooltip);
                }
            });
        };
        document.addEventListener('click', this._qualityTooltipDocClick);

        this._qualityTooltipScroll = () => {
            const open = this.querySelector(
                '[data-quality-tooltip-trigger][aria-expanded="true"]'
            );
            if (!open) return;
            const tooltip = document.getElementById(open.getAttribute('aria-controls') || '');
            if (tooltip && !tooltip.hidden) {
                this.#hideQualityTooltip(/** @type {HTMLButtonElement} */ (open), tooltip);
            }
        };
        window.addEventListener('scroll', this._qualityTooltipScroll, { passive: true });
    }

    /**
     * @param {HTMLButtonElement} trigger
     * @param {HTMLElement} tooltip
     */
    #showQualityTooltip(trigger, tooltip) {
        if (this._currentQualityTooltip && this._currentQualityTooltip !== tooltip) {
            if (this._currentQualityTrigger) {
                this.#hideQualityTooltip(this._currentQualityTrigger, this._currentQualityTooltip);
            }
        }

        trigger.setAttribute('aria-expanded', 'true');
        tooltip.hidden = false;
        this._currentQualityTooltip = tooltip;
        this._currentQualityTrigger = trigger;

        requestAnimationFrame(() => {
            this.#positionQualityTooltip(trigger, tooltip);
        });
    }

    /**
     * @param {HTMLButtonElement} trigger
     * @param {HTMLElement} tooltip
     */
    #hideQualityTooltip(trigger, tooltip) {
        trigger.setAttribute('aria-expanded', 'false');
        tooltip.hidden = true;
        if (this._currentQualityTooltip === tooltip) {
            this._currentQualityTooltip = null;
            this._currentQualityTrigger = null;
        }
    }

    /**
     * @param {HTMLButtonElement} trigger
     * @param {HTMLElement} tooltip
     */
    #positionQualityTooltip(trigger, tooltip) {
        const rect = trigger.getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        let top = rect.top - tooltipRect.height - 8;
        let left = rect.left + rect.width / 2 - tooltipRect.width / 2;

        if (top < 0) {
            top = rect.bottom + 8;
        }

        if (left < 8) {
            left = 8;
        } else if (left + tooltipRect.width > viewportWidth - 8) {
            left = viewportWidth - tooltipRect.width - 8;
        }

        tooltip.style.top = `${top}px`;
        tooltip.style.left = `${left}px`;
    }

    #isTouchDevice() {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }
}

if (!customElements.get('product-multi-option')) {
    customElements.define('product-multi-option', ProductMultiOption);
}
